<!--
  MicrosoftSignInButton.svelte — Custom-styled "Sign in with Microsoft" button.

  Microsoft ships no drop-in button widget for this flow, so the component
  drives the OpenID Connect authorization-code request directly: it opens the
  tenant's authorize endpoint in a popup, and the popup lands on our own
  `/auth/microsoft/callback` route, which posts the code back here.

  A popup rather than a full-page redirect keeps the login page's URL — and
  the app-consent parameters on it — exactly as they were, and matches how the
  Google and Apple buttons behave.

  The `state` value is generated here and checked on the way back, so a
  message naming a different exchange is ignored.

  @prop {string} [text] — 'signin_with' | 'signup_with' | 'continue_with'.
-->
<script lang="ts">
	import { onDestroy } from 'svelte';
	import { Button } from 'flowbite-svelte';
	import { configurationStore } from '../AppConfiguration';
	import { t2 } from '../i18n';

	interface Props {
		/** Text variant shown on the button. */
		text?: 'signin_with' | 'signup_with' | 'continue_with';
		/** Callback fired with the Microsoft authorization code string. */
		oncredential?: (_code: string) => void;
	}

	let { text = 'signin_with', oncredential }: Props = $props();

	/** Discriminator matching what the callback route posts back. */
	const MESSAGE_SOURCE = 'abstractid-microsoft';

	/** Shape of the message the callback route sends to this window. */
	type CallbackMessage = {
		source?: string;
		code?: string | null;
		state?: string | null;
		error?: string | null;
	};

	/** Opaque value echoed by Microsoft, used to match the response to this request. */
	let expectedState = '';

	/** Handle for the popup, so a second click reuses rather than stacks it. */
	let popup: Window | null = null;

	/** Error surfaced when Microsoft declines or the popup cannot open. */
	let errorMessage = $state('');

	/** Whether Microsoft sign-in is configured well enough to attempt. */
	let configured = $derived(
		!!$configurationStore?.microsoftClientId && !!$configurationStore?.microsoftRedirectURI
	);

	/**
	 * Build the tenant's authorization URL for this request.
	 * @returns The fully-qualified authorize endpoint with query parameters.
	 */
	function buildAuthorizeUrl(): string {
		const config = $configurationStore!;
		const params = new URLSearchParams({
			client_id: config.microsoftClientId!,
			response_type: 'code',
			redirect_uri: config.microsoftRedirectURI!,
			response_mode: 'query',
			scope: 'openid profile email',
			state: expectedState
		});
		const tenant = config.microsoftTenantId || 'common';
		return `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/authorize?${params.toString()}`;
	}

	/**
	 * Receive the authorization code from the popup.
	 *
	 * Messages are accepted only from this origin, from our own callback page,
	 * and only when they carry the `state` this component issued.
	 *
	 * @param event - The incoming postMessage event.
	 */
	function handleMessage(event: MessageEvent): void {
		/** Ignore anything not sent by our own callback page. */
		if (event.origin !== window.location.origin) return;

		const data = event.data as CallbackMessage | null;
		/** Ignore unrelated traffic, and any response to an exchange we did not start. */
		if (!data || data.source !== MESSAGE_SOURCE || data.state !== expectedState) return;

		window.removeEventListener('message', handleMessage);
		expectedState = '';

		/** Microsoft reports a declined or failed authorisation in `error`. */
		if (data.error) {
			errorMessage = t2('Microsoft sign-in failed. Please try again.');
			return;
		}

		/** Hand the code up for the server to exchange. */
		if (data.code) oncredential?.(data.code);
	}

	/**
	 * Handle the button click — open the Microsoft authorisation popup.
	 */
	function handleClick(): void {
		errorMessage = '';
		expectedState = crypto.randomUUID();

		window.addEventListener('message', handleMessage);
		popup = window.open(buildAuthorizeUrl(), 'abstractid-microsoft-login', 'width=520,height=680');

		/** A blocked popup never reports back, so say so rather than hanging. */
		if (!popup) {
			window.removeEventListener('message', handleMessage);
			errorMessage = t2('Allow pop-ups for this site to sign in with Microsoft.');
		}
	}

	onDestroy(() => {
		window.removeEventListener('message', handleMessage);
	});

	/** Human-readable label based on the text prop. */
	const labelMap: Record<string, string> = {
		signin_with: t2('Sign in with Microsoft'),
		signup_with: t2('Sign up with Microsoft'),
		continue_with: t2('Continue with Microsoft')
	};
</script>

{#if configured || !$configurationStore}
	<Button color="alternative" class="w-full" disabled={!configured} onclick={handleClick}>
		<svg class="mr-2" width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
			<path fill="#F25022" d="M1 1h10v10H1z" />
			<path fill="#7FBA00" d="M12 1h10v10H12z" />
			<path fill="#00A4EF" d="M1 12h10v10H1z" />
			<path fill="#FFB900" d="M12 12h10v10H12z" />
		</svg>
		{labelMap[text] || t2('Sign in with Microsoft')}
	</Button>
	{#if errorMessage}
		<p class="mt-2 text-center text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
	{/if}
{/if}
